import React from "react";

/**
 * One settled lens as seen at the slit lamp (examiner's view): each
 * quadrant's landing in 30 µm steps, the limbus the Pentacam traced and its
 * steep meridian, the drill dots against it, and where the optic zone has
 * moved off the corneal apex.
 *
 * `fit` is one settled lens from the backend's "Simulated Fit" (version 3):
 * its quadrants are judged over the outer part of the landing, and its zones
 * and dots are where the settled lens puts them. Angles are the map's: 0 deg
 * at 3 o'clock, counter-clockwise; for a right eye nasal is toward 0 deg.
 */

export const STEP_UM = 30;
const DEG = Math.PI / 180;

const NAMES = ["nasal", "superonasal", "superior", "superotemporal", "temporal", "inferotemporal", "inferior", "inferonasal"];
const SIDES = ["right", "upper right", "superior", "upper left", "left", "lower left", "inferior", "lower right"];
export const ADVERB = {
  nasal: "nasally", superonasal: "superonasally", superior: "superiorly", superotemporal: "superotemporally",
  temporal: "temporally", inferotemporal: "inferotemporally", inferior: "inferiorly", inferonasal: "inferonasally",
};

export const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export const fmt = (n) => (typeof n === "number" && Number.isFinite(n) ? Math.round(n).toLocaleString("en-US").replace("-", "−") : "—");
export const mm2 = (n) => (n < 0 ? "−" : "") + Math.abs(n).toFixed(2);
const wrap = (a) => ((a % 360) + 360) % 360;

/** Map angle to a name: anatomical when the eye is known, else as drawn. */
export function anatomical(angle, side) {
  const known = side === "OD" || side === "OS";
  const a = side === "OS" ? wrap(180 - angle) : wrap(angle);
  const i = Math.floor(((a + 22.5) % 360) / 45);
  return known ? NAMES[i] : SIDES[i];
}

/** A landing in words and steps: + lifts, − compresses, within one step aligned. */
export function landing(gap) {
  if (gap == null) return { kind: "none", level: 0, steps: 0, words: "no data", short: "—" };
  const a = Math.abs(gap);
  if (a <= STEP_UM) return { kind: "aligned", level: 0, steps: 0, words: "aligned", short: "✓ aligned" };
  const steps = Math.max(1, Math.round(a / STEP_UM));
  const level = steps <= 2 ? 1 : steps <= 5 ? 2 : 3;
  const unit = steps === 1 ? "step" : "steps";
  return gap > 0
    ? { kind: "lift", level, steps, words: `lifts ${steps} ${unit}`, short: `↑ lifts ${steps}` }
    : { kind: "comp", level, steps, words: `compresses ${steps} ${unit}`, short: `↓ compresses ${steps}` };
}

const BASE = { lift: "#2a78d6", comp: "#e34948" };
const ALIGNED = "#d4d3ce";
const NO_DATA = "#eceef1";
function mix(hex, p) {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `#${c.map((v) => Math.round(v * p + 255 * (1 - p)).toString(16).padStart(2, "0")).join("")}`;
}
export function shade(v) {
  if (v.kind === "aligned") return ALIGNED;
  if (v.kind === "none") return NO_DATA;
  return mix(BASE[v.kind], [0.32, 0.62, 1][v.level - 1]);
}

/** The quadrants in anatomical order, each with its verdict. */
export function judged(fit, side) {
  return (fit.quadrants || [])
    .map((q) => ({ ...q, where: anatomical(q.centre, side), verdict: landing(q.gap) }))
    .sort((a, b) => NAMES.indexOf(a.where) - NAMES.indexOf(b.where));
}

/** One sentence on the landing. */
export function landingSummary(fit, side) {
  const qs = judged(fit, side);
  const off = qs.filter((q) => q.verdict.kind === "lift" || q.verdict.kind === "comp")
    .sort((a, b) => b.verdict.steps - a.verdict.steps);
  if (!qs.length) return "No landing to judge at this diameter.";
  if (!off.length) return "Lands evenly: every quadrant within one step.";
  const parts = off.map((q, i) => `${i ? q.where : cap(q.where)} ${q.verdict.words}`);
  const rest = off.length < qs.length ? `; the rest within one step` : "";
  return `${parts.join(", ")}${rest}.`;
}

/** One sentence on how a lens settles. */
export function settleSummary(fit, side) {
  if (fit.clearance_under_apex <= 0) {
    return `No clearance under the lens apex (${fmt(fit.clearance_under_apex)} µm, aim ${fit.clearance_target}): ` +
      "it would rest on the cornea, so it cannot settle as drawn.";
  }
  const clearance = `${fmt(fit.clearance_under_apex)} µm under the lens apex (aim ${fit.clearance_target}).`;
  if (fit.tilt_deg < 0.3 || fit.dips_toward == null) return `Settles level and centred. ${clearance}`;
  const dips = anatomical(fit.dips_toward, side);
  const moves = fit.optic_toward == null ? null : anatomical(fit.optic_toward, side);
  const shift = moves && fit.optic_shift_mm >= 0.05
    ? `, carrying its optic zone ${fit.optic_shift_mm.toFixed(2)} mm ${ADVERB[moves] || `toward the ${moves}`}`
    : "";
  return `Tips ${fit.tilt_deg.toFixed(1)}° into the ${dips} side${shift}. ${clearance}`;
}

/** A settled lens: the drawing, and beside it what it does, quadrant by quadrant. */
export function FitView({ title, what, fit, option, limbus, side, dotTarget, children }) {
  return (
    <article className="sf-view">
      <div className="sf-view-draw">
        {fit && <SlitLampFit fit={fit} option={option} limbus={limbus} side={side} dotTarget={dotTarget} label={title} />}
      </div>
      <div className="sf-view-text">
        <h3 className="sf-view-title">{title}</h3>
        {what && <p className="sf-view-what">{what}</p>}
        {children}
        {!fit ? <p className="sf-note">Not enough of the map at this diameter to settle the lens.</p> : (
          <>
            <p className="sf-verdict">{landingSummary(fit, side)}</p>
            <p className="sf-settle">{settleSummary(fit, side)}</p>
            <table className="sf-quads">
              <thead><tr><th scope="col">Quadrant</th><th scope="col">Order</th><th scope="col">Landing</th></tr></thead>
              <tbody>
                {judged(fit, side).map((q) => (
                  <tr key={q.centre} title={q.lo != null ? `Across the quadrant: ${fmt(q.lo)} to ${fmt(q.hi)} µm` : undefined}>
                    <td>{cap(q.where)}</td>
                    <td className="sf-order">{q.steps}</td>
                    <td><span className="sf-sw" style={{ background: shade(q.verdict) }} />{q.verdict.words}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </article>
  );
}

const W = 500;
const H = 392;
const R = 138;                       // the lens edge, px
const CX = W / 2;
const CY = 196;

function band(cx, cy, r0, r1, a0, a1) {
  const p = (r, a) => [cx + r * Math.cos(a * DEG), cy - r * Math.sin(a * DEG)];
  const large = a1 - a0 > 180 ? 1 : 0;
  const [x0, y0] = p(r1, a0), [x1, y1] = p(r1, a1), [x2, y2] = p(r0, a1), [x3, y3] = p(r0, a0);
  return `M${x0} ${y0}A${r1} ${r1} 0 ${large} 0 ${x1} ${y1}L${x2} ${y2}A${r0} ${r0} 0 ${large} 1 ${x3} ${y3}Z`;
}

export default function SlitLampFit({ fit, option, limbus, side, dotTarget = [0, 0.5], label }) {
  const k = R / (option.diameter / 2);                       // px per mm
  const X = (x) => CX + x * k;
  const Y = (y) => CY - y * k;
  const at = (c, r, a) => [X(c[0]) + r * Math.cos(a * DEG), Y(c[1]) - r * Math.sin(a * DEG)];
  const centres = fit.centres || {};
  const cEdge = centres.edge || [0, 0], cLcc = centres.lcc || [0, 0], cMp = centres.midperipheral || [0, 0];
  const optic = centres.optic || [0, 0];
  const rLcc = (option.lcc / 2) * k, rMp = (option.midperipheral_zone / 2) * k;
  const qs = judged(fit, side);
  const nasalRight = side !== "OS";
  const ink = "#12181f", ink2 = "#4a505a";

  const be = fit.sag && fit.sag.flat != null ? fit.sag.steep - fit.sag.flat : 0;
  const limbalAxis = fit.limbal ? fit.limbal["steep axis"] : null;

  const lx = limbus ? X(limbus.cx) : null, ly = limbus ? Y(limbus.cy) : null, lr = limbus ? (limbus.d / 2) * k : null;
  const dots = (fit.dots || []).map((d) => {
    const inside = d.clearance >= dotTarget[0] && d.clearance <= dotTarget[1];
    const x = X(d.x), y = Y(d.y);
    // the number sits just inside the limbus, on the dot's own line from the lens centre
    let tx = x, ty = y;
    if (limbus) {
      const ux = Math.cos(d.angle * DEG), uy = -Math.sin(d.angle * DEG);
      const dx = lx - X(cLcc[0]), dy = ly - Y(cLcc[1]), along = ux * dx + uy * dy;
      const toLimbus = along + Math.sqrt(Math.max(0, along * along - (dx * dx + dy * dy) + lr * lr));
      [tx, ty] = at(cLcc, Math.min(toLimbus, rMp) - 15, d.angle);
    }
    return { ...d, inside, x, y, tx, ty };
  });

  const shiftShown = fit.optic_toward != null && fit.optic_shift_mm >= 0.05;
  const arrowLen = 36;
  const [ax, ay] = shiftShown ? [X(0) + arrowLen * Math.cos(fit.optic_toward * DEG), Y(0) - arrowLen * Math.sin(fit.optic_toward * DEG)] : [0, 0];
  const head = (a) => [ax + 7 * Math.cos((fit.optic_toward + a) * DEG), ay - 7 * Math.sin((fit.optic_toward + a) * DEG)];
  const labelBelow = shiftShown && Math.sin(fit.optic_toward * DEG) > 0;

  const description = `${label ? `${label}: ` : ""}${landingSummary(fit, side)} ${settleSummary(fit, side)}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="slf-svg" role="img" aria-label={description}>
      <circle cx={X(cEdge[0])} cy={Y(cEdge[1])} r={R + 12} fill="#f4efe9" />
      {limbus && <circle cx={lx} cy={ly} r={lr} fill="#e8edf2" />}

      {/* the limbal clearance zone, from where the base curve ends (dashed) to where the landing begins */}
      <path
        d={`M${X(cLcc[0]) - rLcc} ${Y(cLcc[1])}a${rLcc} ${rLcc} 0 1 0 ${2 * rLcc} 0a${rLcc} ${rLcc} 0 1 0 ${-2 * rLcc} 0Z` +
           `M${X(cMp[0]) - rMp} ${Y(cMp[1])}a${rMp} ${rMp} 0 1 1 ${2 * rMp} 0a${rMp} ${rMp} 0 1 1 ${-2 * rMp} 0Z`}
        fill={ink} fillOpacity={0.06} fillRule="evenodd"
      />
      <circle cx={X(cMp[0])} cy={Y(cMp[1])} r={rMp} fill="none" stroke="#7d838c" strokeWidth={1} strokeDasharray="3 3" />

      {/* the landing, quadrant by quadrant */}
      {qs.map((q) => {
        const gap = (2 / (rLcc + 1)) / DEG;
        return <path key={`q${q.centre}`} d={band(X(cEdge[0]), Y(cEdge[1]), rLcc + 1, R, q.centre - 45 + gap / 2, q.centre + 45 - gap / 2)}
                     fill={shade(q.verdict)} />;
      })}

      {/* the limbus over it: the circle through the points the Pentacam traced */}
      {limbus && <circle cx={lx} cy={ly} r={lr} fill="none" stroke={ink2} strokeWidth={1.25} strokeOpacity={0.85} />}
      {limbus && (limbus.points || []).map(([x, y], i) => <circle key={`p${i}`} cx={X(x)} cy={Y(y)} r={1.3} fill={ink} />)}
      <circle cx={X(cEdge[0])} cy={Y(cEdge[1])} r={R} fill="none" stroke={ink2} strokeWidth={1} />
      <circle cx={X(cLcc[0])} cy={Y(cLcc[1])} r={rLcc} fill="none" stroke={ink2} strokeWidth={1} />

      {/* the bi-elevation's steep meridian, across the zone where it acts */}
      {be > 0 && [fit.steep, fit.steep + 180].map((a) => {
        const [x0, y0] = at(cLcc, rMp + 2, a), [x1, y1] = at(cLcc, rLcc - 2, a);
        return <line key={`be${a}`} x1={x0} y1={y0} x2={x1} y2={y1} stroke={ink} strokeWidth={4} strokeLinecap="round" />;
      })}
      {/* the limbus's own steep meridian */}
      {limbalAxis != null && [limbalAxis, limbalAxis + 180].map((a) => {
        const [x0, y0] = at(cLcc, rMp * 0.62, a), [x1, y1] = at(cLcc, rLcc + 4, a);
        return <line key={`la${a}`} x1={x0} y1={y0} x2={x1} y2={y1} stroke={ink} strokeWidth={1.4} strokeDasharray="5 3" />;
      })}

      {/* drill dots: filled when 0-0.5 mm outside the limbus, open when not */}
      {dots.map((d) => (
        <g key={`d${d.angle}`}>
          <circle cx={d.x} cy={d.y} r={3.8} fill={d.inside ? ink : "#fff"} stroke={ink} strokeWidth={1.5}>
            <title>{`Drill dot ${mm2(d.clearance)} mm outside the limbus`}</title>
          </circle>
          {limbus && <text x={d.tx} y={d.ty + 3.5} textAnchor="middle" className="slf-dot">{mm2(d.clearance)}</text>}
        </g>
      ))}

      {/* the corneal apex, and where the optic zone has gone */}
      <path d={`M${X(0) - 6} ${Y(0)}H${X(0) + 6}M${X(0)} ${Y(0) - 6}V${Y(0) + 6}`} stroke={ink2} strokeWidth={1.4} />
      {shiftShown && (
        <g>
          <line x1={X(0)} y1={Y(0)} x2={ax} y2={ay} stroke={ink} strokeWidth={2} strokeLinecap="round" />
          <path d={`M${head(150)[0]} ${head(150)[1]}L${ax} ${ay}L${head(-150)[0]} ${head(-150)[1]}`} fill="none" stroke={ink}
                strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          <text x={X(0)} y={labelBelow ? Y(0) + 20 : Y(0) - 11} textAnchor="middle" className="slf-shift">
            optic zone {fit.optic_shift_mm.toFixed(2)} mm
          </text>
        </g>
      )}
      <circle cx={X(optic[0])} cy={Y(optic[1])} r={3.4} fill={ink} />

      {/* orientation */}
      <g className="slf-orient">
        <text x={22} y={15} textAnchor="middle">S</text>
        <text x={22} y={43} textAnchor="middle">I</text>
        <text x={10} y={29} textAnchor="middle">{side ? (nasalRight ? "T" : "N") : ""}</text>
        <text x={34} y={29} textAnchor="middle">{side ? (nasalRight ? "N" : "T") : ""}</text>
        <path d="M16 25.5H28M22 19.5V31.5" stroke="#c2c7ce" strokeWidth={1} />
      </g>

      {/* each quadrant's order and its landing, outside the lens */}
      {qs.map((q) => {
        const c = Math.cos(q.centre * DEG), s = Math.sin(q.centre * DEG);
        const [x, y] = at(cEdge, R + 14, q.centre);
        const anchor = c > 0.38 ? "start" : c < -0.38 ? "end" : "middle";
        const top = s > 0.38 ? y - 24 : s < -0.38 ? y + 4 : y - 10;
        return (
          <g key={`l${q.centre}`}>
            <text x={x} y={top + 12} textAnchor={anchor} className="slf-order">{q.steps}</text>
            <text x={x} y={top + 27} textAnchor={anchor} className="slf-verdict">{q.verdict.short}</text>
          </g>
        );
      })}
    </svg>
  );
}
